import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { SslExpiryPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'
import { breadcrumbList, jsonLdScript, webPage } from '@/lib/schema'

export const Route = createFileRoute('/_site/features/ssl-monitoring')({
  component: SslMonitoringPage,
  head: () => ({
    meta: [
      { title: 'SSL Certificate Monitoring — Expiry Alerts | Uptrack' },
      { name: 'description', content: 'Monitor SSL certificate expiry and get alerts 14, 30, or 60 days before renewal. Track issuer, chain, and validity. Included with every paid plan.' },
      { property: 'og:title', content: 'SSL Certificate Monitoring — Expiry Alerts | Uptrack' },
      { property: 'og:description', content: 'SSL expiry alerts at 14/30/60 days. Track certificate chain and issuer. Included with every paid plan.' },
      { property: 'og:image', content: 'https://uptrack.app/og/features-ssl-monitoring.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/features/ssl-monitoring' },
    ],
    scripts: [
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'Features', url: 'https://uptrack.app/features' },
        { name: 'SSL Monitoring', url: 'https://uptrack.app/features/ssl-monitoring' },
      ])),
      jsonLdScript(webPage({
        name: 'Uptrack SSL Certificate Monitoring',
        description: 'Monitor SSL certificate expiry dates and get alerts before certificates lapse.',
        url: 'https://uptrack.app/features/ssl-monitoring',
      })),
    ],
  }),
})

function SslMonitoringPage() {
  return (
    <FeaturePage
      section='SSL certificate monitoring'
      title='SSL certificate monitoring before expiry hits.'
      intro='Get alerted 14, 30, or 60 days before your SSL certificate expires. Track issuer changes, certificate chain, and validity — included with every paid plan.'
      featuresHeading='Never let a certificate expire silently'
      features={[
    { title: 'Configurable expiry alerts', desc: 'Choose when to be notified: 14, 30, or 60 days before expiry. Enough lead time for any renewal workflow.' },
    { title: 'Chain and issuer tracking', desc: 'Detect unexpected issuer changes or broken certificate chains that could cause browser warnings.' },
    { title: 'Included with paid plans', desc: 'SSL monitoring is included on every paid plan. No add-on fees.' },
    { title: 'Instant mismatch detection', desc: 'Get alerted if the certificate domain does not match the monitored hostname.' },
      ]}
      table={{
        heading: 'Uptrack vs the alternatives',
        intro: 'Most tools either charge extra for SSL monitoring or offer limited alert thresholds.',
        columns: ['Provider', 'SSL Included', 'Expiry Alerts', 'Chain Check'],
        rows: [
      { cells: ['UptimeRobot', 'Paid only', '1 threshold', 'No'] },
      { cells: ['Better Stack', 'Yes', '1 threshold', 'No'] },
      { cells: ['Pingdom', 'Yes', 'Limited', 'No'] },
      { cells: ['Uptrack', 'All paid plans', '14/30/60 days', 'Yes'], highlight: true },
        ],
      }}
      why={{ heading: 'Why SSL monitoring matters', body: 'An expired SSL certificate triggers browser security warnings, blocks API traffic, and breaks customer trust. Let\'s Encrypt certs last 90 days — automated renewal can silently fail. SSL monitoring catches the gap before your users do.' }}
      cta={{ heading: 'Catch SSL expiry before your users do', body: 'Every paid plan includes 30-second checks and alert confirmation.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/monitors">
          <SslExpiryPiece />
        </ProductFrame>
      }
    />
  )
}
